import React from 'react';
import type { Emitter } from 'mitt';
import { useDispatch } from 'react-redux';
import type { ProposalStatus } from '../../../api/agentChat/interface';
import { fetchMessages } from '../../../api/agentChat/threads/client';
import { fetchArtifact } from '../../../api/artifacts/client';
import { fetchEvents } from '../../../api/events/client';
import { globalBus } from '../../../context/event-bus';
import {
  injectArtifactAction,
  removeArtifactAction,
} from '../../../data/artifacts/logic';
import {
  NOTIFICATION_EMIT_EVENTS,
  type NotificationEmitEvents,
} from '../../../data/notifications/logic';
import {
  IMMUTABLE_PROPOSAL_STATUSES,
  type DisplayMessage,
  type DisplayRole,
  reconcileProposals,
} from '../chatHelpers';

// Typed view of the runtime-untyped globalBus for the chat-related events
// this hook subscribes to.
const chatBus = globalBus as unknown as Emitter<NotificationEmitEvents>;

// Subscribes to streaming + lifecycle events on the chat bus and applies them
// to `displayMessages` via the passed setter. `displayMessagesRef` is used by
// the timeline-updated handler to decide whether reconciliation is worth a
// network round trip (no approved proposals → skip).
export function useChatStreamSubscriptions(
  dossierId: string,
  threadId: string,
  setDisplayMessages: React.Dispatch<React.SetStateAction<DisplayMessage[]>>,
  displayMessagesRef: React.MutableRefObject<DisplayMessage[]>,
  onArtifactOpen?: (artifactId: string) => void,
  onArtifactReplace?: (oldArtifactId: string, newArtifactId: string) => void
): void {
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!dossierId) {
      return undefined;
    }

    // Tracks the most recent chunk seq per messageId so out-of-order or
    // replayed chunks are ignored.
    const lastSeqByMessage: Record<string, number> = {};

    const onCreated = () => {};

    const matchesThread = (data: {
      dossierId: string;
      threadId: string;
    }): boolean => data.dossierId === dossierId && data.threadId === threadId;

    const onStarted = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.ChatMessageStarted]
    ) => {
      if (!matchesThread(data)) {
        return;
      }
      setDisplayMessages((prev) => {
        if (prev.some((m) => m.message_id === threadId)) {
          return prev.map((m) =>
            m.message_id === threadId ? { ...m, text: '', streaming: true } : m
          );
        }
        return [
          ...prev,
          {
            message_id: threadId,
            role: data.role as DisplayRole,
            text: '',
            streaming: true,
          },
        ];
      });
      lastSeqByMessage[threadId] = -1;
    };

    const onChunk = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.ChatChunk]
    ) => {
      if (!matchesThread(data)) {
        return;
      }
      const lastSeq = lastSeqByMessage[threadId] ?? -1;
      if (data.seq <= lastSeq) {
        return;
      }
      lastSeqByMessage[threadId] = data.seq;
      setDisplayMessages((prev) =>
        prev.map((m) =>
          m.message_id === threadId
            ? { ...m, text: m.text + data.chunkText }
            : m
        )
      );
    };

    const onSchema = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.ChatSchema]
    ) => {
      if (!matchesThread(data)) {
        return;
      }
      setDisplayMessages((prev) =>
        prev.map((m) =>
          m.message_id === threadId ? { ...m, artifact_id: threadId } : m
        )
      );
      dispatch(
        injectArtifactAction(dossierId, {
          artifact_id: threadId,
          dossier_id: dossierId,
          claim_id: null,
          schema_name: 'adhoc',
          data: {},
          created_at: null,
          event_id: null,
          data_schema: data.dataSchema,
          ui_schema: data.uiSchema,
        })
      );
      onArtifactOpen?.(threadId);
    };

    const lastPatchSeqByMessage: Record<string, number> = {};

    const onPatch = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.ChatPatch]
    ) => {
      if (!matchesThread(data)) {
        return;
      }
      const lastSeq = lastPatchSeqByMessage[threadId] ?? -1;
      if (data.seq <= lastSeq) {
        return;
      }
      lastPatchSeqByMessage[threadId] = data.seq;
      dispatch(
        injectArtifactAction(dossierId, {
          artifact_id: threadId,
          data: data.data,
          data_schema: data.dataSchema,
          ui_schema: data.uiSchema,
        })
      );
    };

    const onComplete = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.ChatMessageComplete]
    ) => {
      if (!matchesThread(data)) {
        return;
      }
      // Replace the thread-keyed placeholder with the real persisted message id.
      setDisplayMessages((prev) =>
        prev.map((m) =>
          m.message_id === threadId
            ? { ...m, message_id: data.messageId, streaming: false }
            : m
        )
      );
      delete lastSeqByMessage[threadId];
      delete lastPatchSeqByMessage[threadId];
      const completedMessageId = data.messageId;
      void (async () => {
        try {
          const res = await fetchMessages(dossierId, threadId);
          let pendingArtifact: {
            artifactId: string;
            messageId: string;
            artifactName?: string | null;
          } | null = null;
          setDisplayMessages((prev) => {
            const next = [...prev];
            for (const fresh of res.messages) {
              const idx = next.findIndex(
                (m) => m.message_id === fresh.message_id
              );
              if (idx >= 0) {
                const existing = next[idx].proposals;
                const alreadyActedOn = existing?.some(
                  (p) => p.status !== 'proposed'
                );
                if (!alreadyActedOn) {
                  next[idx] = { ...next[idx], proposals: fresh.proposals };
                }
                if (fresh.artifact_id) {
                  if (fresh.message_id === completedMessageId) {
                    pendingArtifact = {
                      artifactId: fresh.artifact_id,
                      messageId: fresh.message_id,
                      artifactName: fresh.artifact_name,
                    };
                  } else {
                    next[idx] = {
                      ...next[idx],
                      artifact_id: fresh.artifact_id,
                      artifact_name: fresh.artifact_name,
                    };
                  }
                }
              }
            }
            return next;
          });
          if (pendingArtifact) {
            const { artifactId, messageId, artifactName } = pendingArtifact;
            const artifact = await fetchArtifact(dossierId, artifactId);
            dispatch(injectArtifactAction(dossierId, artifact));
            dispatch(removeArtifactAction(dossierId, threadId));
            setDisplayMessages((prev) =>
              prev.map((m) =>
                m.message_id === messageId
                  ? {
                      ...m,
                      artifact_id: artifactId,
                      artifact_name: artifactName,
                    }
                  : m
              )
            );
            onArtifactReplace?.(threadId, artifactId);
          } else {
            // No artifact was persisted — clear the placeholder chip and Redux entry
            dispatch(removeArtifactAction(dossierId, threadId));
            setDisplayMessages((prev) =>
              prev.map((m) =>
                m.message_id === completedMessageId &&
                m.artifact_id === threadId
                  ? { ...m, artifact_id: undefined }
                  : m
              )
            );
          }
        } catch {
          // ignore
        }
      })();
    };

    const onProposalStatus = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.ChatProposalStatusChanged]
    ) => {
      if (!matchesThread(data)) {
        return;
      }
      setDisplayMessages((prev) =>
        prev.map((m) => {
          if (m.message_id !== data.messageId || !m.proposals) {
            return m;
          }
          return {
            ...m,
            proposals: m.proposals.map((p) =>
              p.proposal_id === data.proposalId
                ? {
                    ...p,
                    status: data.status as ProposalStatus,
                    result: data.result ?? p.result,
                    error: data.error ?? p.error,
                  }
                : p
            ),
          };
        })
      );
    };

    const updateProposalStatus = (
      proposal_id: string,
      status: ProposalStatus,
      summary?: string
    ) => {
      setDisplayMessages((prev) =>
        prev.map((m) => {
          if (!m.proposals?.some((p) => p.proposal_id === proposal_id)) {
            return m;
          }
          return {
            ...m,
            proposals: m.proposals!.map((p) => {
              if (
                p.proposal_id !== proposal_id ||
                IMMUTABLE_PROPOSAL_STATUSES.includes(p.status)
              ) {
                return p;
              }
              return { ...p, status, result: summary ?? p.result };
            }),
          };
        })
      );
    };

    const onEventProcessing = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.EventProcessing]
    ) => {
      if (data.dossierId !== dossierId) {
        return;
      }
      if (data.eventType !== 'user_directive_received') {
        return;
      }
      const { proposal_id } = data.metadata as { proposal_id?: string };
      if (!proposal_id) {
        return;
      }
      updateProposalStatus(proposal_id, 'executing');
    };

    const onEventProcessed = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.EventProcessed]
    ) => {
      if (data.dossierId !== dossierId) {
        return;
      }
      if (data.eventType !== 'user_directive_received') {
        return;
      }
      const { proposal_id, summary } = data.metadata as {
        proposal_id?: string;
        summary?: string;
      };
      if (!proposal_id) {
        return;
      }
      updateProposalStatus(proposal_id, 'executed', summary);
    };

    const onEventProcessingFailed = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.EventProcessingFailed]
    ) => {
      if (data.dossierId !== dossierId) {
        return;
      }
      if (data.eventType !== 'user_directive_received') {
        return;
      }
      const { proposal_id } = data.metadata as { proposal_id?: string };
      if (!proposal_id) {
        return;
      }
      updateProposalStatus(proposal_id, 'failed');
    };

    const onEventCancelled = (
      data: NotificationEmitEvents[NOTIFICATION_EMIT_EVENTS.EventCancelled]
    ) => {
      if (data.dossierId !== dossierId) {
        return;
      }
      if (data.eventType !== 'user_directive_received') {
        return;
      }
      const { proposal_id } = data.metadata as { proposal_id?: string };
      if (!proposal_id) {
        return;
      }
      updateProposalStatus(proposal_id, 'cancelled');
    };

    const onTimelineUpdated = (eventDossierId: string) => {
      if (eventDossierId !== dossierId) {
        return;
      }
      if (
        !displayMessagesRef.current.some((m) =>
          m.proposals?.some((p) => p.status === 'approved')
        )
      ) {
        return;
      }
      fetchEvents(dossierId)
        .then((events) => {
          setDisplayMessages((prev) => reconcileProposals(prev, events));
          return undefined;
        })
        .catch(() => {});
    };

    chatBus.on(NOTIFICATION_EMIT_EVENTS.ChatMessageCreated, onCreated);
    chatBus.on(NOTIFICATION_EMIT_EVENTS.ChatMessageStarted, onStarted);
    chatBus.on(NOTIFICATION_EMIT_EVENTS.ChatChunk, onChunk);
    chatBus.on(NOTIFICATION_EMIT_EVENTS.ChatSchema, onSchema);
    chatBus.on(NOTIFICATION_EMIT_EVENTS.ChatPatch, onPatch);
    chatBus.on(NOTIFICATION_EMIT_EVENTS.ChatMessageComplete, onComplete);
    chatBus.on(
      NOTIFICATION_EMIT_EVENTS.ChatProposalStatusChanged,
      onProposalStatus
    );
    chatBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessing, onEventProcessing);
    chatBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessed, onEventProcessed);
    chatBus.on(
      NOTIFICATION_EMIT_EVENTS.EventProcessingFailed,
      onEventProcessingFailed
    );
    chatBus.on(NOTIFICATION_EMIT_EVENTS.EventCancelled, onEventCancelled);
    chatBus.on(NOTIFICATION_EMIT_EVENTS.TimelineUpdated, onTimelineUpdated);

    return () => {
      chatBus.off(NOTIFICATION_EMIT_EVENTS.ChatMessageCreated, onCreated);
      chatBus.off(NOTIFICATION_EMIT_EVENTS.ChatMessageStarted, onStarted);
      chatBus.off(NOTIFICATION_EMIT_EVENTS.ChatChunk, onChunk);
      chatBus.off(NOTIFICATION_EMIT_EVENTS.ChatSchema, onSchema);
      chatBus.off(NOTIFICATION_EMIT_EVENTS.ChatPatch, onPatch);
      chatBus.off(NOTIFICATION_EMIT_EVENTS.ChatMessageComplete, onComplete);
      chatBus.off(
        NOTIFICATION_EMIT_EVENTS.ChatProposalStatusChanged,
        onProposalStatus
      );
      chatBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessing, onEventProcessing);
      chatBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessed, onEventProcessed);
      chatBus.off(
        NOTIFICATION_EMIT_EVENTS.EventProcessingFailed,
        onEventProcessingFailed
      );
      chatBus.off(NOTIFICATION_EMIT_EVENTS.EventCancelled, onEventCancelled);
      chatBus.off(NOTIFICATION_EMIT_EVENTS.TimelineUpdated, onTimelineUpdated);
    };
  }, [
    dossierId,
    threadId,
    setDisplayMessages,
    displayMessagesRef,
    onArtifactOpen,
    onArtifactReplace,
  ]);
}
